import isEmpty from "lodash-es/isEmpty";
import type { ContentItem } from "./contentItemSchemas";
import { CONTENT_ITEM_KEYS } from "@/constants/contentItemKeys";
import { isRecord } from "./shared";
import { parseConditionalCta } from "./conditionalCta";
import { parseImageContent, parsePersonalizedImageContent } from "./image";
import { parseTextContent } from "./text";
import { parseContentFromSection } from "./contentFrom";
import { parseButton, parseButtonOverlay } from "./cta";
import { parseTableContent } from "./table";
import { parseGridContent } from "./grid";
import { parseChartContent } from "./chart";
import {
  parseMonthlyOverview,
  parseMonthOverview,
  parseMonthOverviewDetails,
} from "./monthOverview";
import { parseDataDistributionContent } from "./dataDistribution";

/** Dispatcher: same branch order as ContentSectionDeserializer.deserializeKeys. */
export const parseContentItem = (
  item: unknown,
  formLabel?: string,
): ContentItem => {
  if (!isRecord(item)) {
    return parseTextContent({ [CONTENT_ITEM_KEYS.TEXT]: "" }, formLabel);
  }
  if (CONTENT_ITEM_KEYS.IMAGE in item && !(CONTENT_ITEM_KEYS.WIDTH in item)) {
    return parseImageContent(item, formLabel);
  }
  if (CONTENT_ITEM_KEYS.IMAGE in item && CONTENT_ITEM_KEYS.WIDTH in item) {
    return parsePersonalizedImageContent(item, formLabel);
  }
  if (
    CONTENT_ITEM_KEYS.OPTIONS in item &&
    Array.isArray(item[CONTENT_ITEM_KEYS.OPTIONS])
  ) {
    return parseConditionalCta(item, formLabel);
  }
  if (
    CONTENT_ITEM_KEYS.TEXT in item &&
    CONTENT_ITEM_KEYS.KEY in item &&
    CONTENT_ITEM_KEYS.YEAR in item
  ) {
    return parseButtonOverlay(item, formLabel);
  }
  if (CONTENT_ITEM_KEYS.TEXT in item && CONTENT_ITEM_KEYS.KEY in item) {
    return parseButton(item, formLabel);
  }
  if (
    CONTENT_ITEM_KEYS.COLUMNS in item ||
    CONTENT_ITEM_KEYS.ROWS in item ||
    CONTENT_ITEM_KEYS.COLUMN_NAMES in item
  ) {
    return parseTableContent(item, formLabel);
  }
  if (CONTENT_ITEM_KEYS.CELLS in item || CONTENT_ITEM_KEYS.CELL in item) {
    return parseGridContent(item, formLabel);
  }
  if (CONTENT_ITEM_KEYS.CHART_TYPE in item) {
    return parseChartContent(item, formLabel);
  }
  if (
    (CONTENT_ITEM_KEYS.MONEY_IN in item &&
      CONTENT_ITEM_KEYS.MONEY_OUT in item) ||
    (formLabel === "monthlyOverview" && CONTENT_ITEM_KEYS.CONTENT_FROM in item)
  ) {
    return parseMonthlyOverview(item, formLabel);
  }
  if (
    CONTENT_ITEM_KEYS.SAVINGS_VARIATION_LINK in item ||
    CONTENT_ITEM_KEYS.DEBT_VARIATION_LINK in item
  ) {
    return parseMonthOverview(item, formLabel);
  }
  if (CONTENT_ITEM_KEYS.MONTH_VARIABLE in item) {
    return parseMonthOverviewDetails(item, formLabel);
  }
  if (CONTENT_ITEM_KEYS.LABEL in item) {
    return parseDataDistributionContent(item, formLabel);
  }
  if (CONTENT_ITEM_KEYS.CONTENT_FROM in item) {
    const cf = isRecord(item)
      ? item[CONTENT_ITEM_KEYS.CONTENT_FROM]
      : undefined;
    if (isEmpty(cf) && CONTENT_ITEM_KEYS.TEXT in item)
      return parseTextContent(item, formLabel);
    return parseContentFromSection(item, formLabel);
  }
  return parseTextContent(item, formLabel);
};
